import { useMemo, useState } from "react";
import type { ColorRecord } from "../lib/color-engine";
import { generateSixtyThirtyTenPalette } from "../lib/palette-generator";
import { buildDealerMessage, buildWhatsAppLink } from "../lib/dealer-export";

interface SixtyThirtyTenPanelProps {
  baseColor: ColorRecord | null;
  catalog: ColorRecord[];
}

/**
 * NOTE: because the catalog is a curated set of architectural neutrals
 * (not a full-saturation color wheel — see REVISED_SPEC.md 3a), the Focus
 * shade this generates is itself a muted, grounding tone rather than a
 * bright pop color. For very similar Base colors, Focus can land on the
 * same nearby dark neutral — that's an honest consequence of the palette
 * being neutrals-only, not a bug in the matching logic.
 */
export function SixtyThirtyTenPanel({ baseColor, catalog }: SixtyThirtyTenPanelProps) {
  const [copied, setCopied] = useState(false);

  const palette = useMemo(() => {
    if (!baseColor || catalog.length === 0) return null;
    try {
      return generateSixtyThirtyTenPalette(baseColor, catalog);
    } catch {
      return null;
    }
  }, [baseColor, catalog]);

  if (!baseColor) {
    return (
      <p className="text-sm text-[#6B6660]">
        Pick a color to auto-generate a 60:30:10 palette from it.
      </p>
    );
  }

  if (!palette) {
    return (
      <p className="text-sm text-[#6B6660]">
        Couldn't generate a palette — the catalog may still be loading.
      </p>
    );
  }

  const rows: Array<{ role: string; pct: string; color: ColorRecord }> = [
    { role: "Base", pct: "60%", color: palette.base },
    { role: "Structure", pct: "30%", color: palette.structure },
    { role: "Focus", pct: "10%", color: palette.focus },
  ];

  const handleSendToDealer = () => {
    const message = buildDealerMessage(palette);
    window.open(buildWhatsAppLink(message), "_blank", "noopener,noreferrer");
  };

  const handleCopy = async () => {
    const message = buildDealerMessage(palette);
    await navigator.clipboard.writeText(message);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col overflow-hidden rounded-md border border-[#D9D5CC]">
        {rows.map(({ role, pct, color }) => (
          <div key={role} className="flex items-center gap-3 p-3">
            <span
              className="h-10 w-10 shrink-0 rounded-md border border-[#D9D5CC]"
              style={{ backgroundColor: color.hex }}
              aria-hidden="true"
            />
            <div className="flex min-w-0 flex-col">
              <span className="text-sm font-medium text-[#232220]">
                {role} · {pct}
              </span>
              <span className="truncate text-xs text-[#6B6660]">
                {color.name} — <span className="font-mono">{color.shadeCode}</span>
              </span>
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={handleSendToDealer}
          className="min-h-[44px] rounded-md bg-[#25D366] px-4 text-sm font-medium text-white"
        >
          Send to dealer via WhatsApp
        </button>
        <button
          type="button"
          onClick={handleCopy}
          className="min-h-[44px] rounded-md border border-[#D9D5CC] px-4 text-sm font-medium text-[#232220] hover:bg-[#EDEBE6]"
        >
          {copied ? "Copied!" : "Copy details"}
        </button>
      </div>
    </div>
  );
}
